import { useCreateContractContext, useUserLanguage } from '@/customHooks';
import textsData from '@/textData/CreateContractsTexts.json'
import { contractLeaseTermMapper } from '@/helpers';

// Icons
import personIcon from '@/assets/contract-icons/person.svg'
import calendarIcon from '@/assets/contract-icons/calendar-icon.svg'
import rentIcon from '@/assets/contract-icons/montly-rent-icon.svg'
import depositIcon from '@/assets/contract-icons/deposits-held-icon.svg'
import unitsIcon from '@/assets/contract-icons/units-icon.svg'
import termIcon from '@/assets/contract-icons/fixed-term-icon.svg'
import addressIcon from '@/assets/map-icon.svg'

interface SummaryItemProps {
  label: string;
  value: string | number | string[];
  icon?: string;
  isFullWidth?: boolean;
}

const SummaryItem = ({ label, value, icon, isFullWidth }: SummaryItemProps) => {
  return (
    <div className={`flex flex-col gap-1 p-3 rounded-lg bg-[var(--color-background-2)] ${isFullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center gap-2">
        {icon && <img src={icon} alt="" className="w-4 h-4 opacity-60" />}
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-medium text-[var(--color-text)]">
        {Array.isArray(value) ? (
          <div className="flex flex-wrap gap-2 mt-1">
            {value.map((v, i) => (
              <span key={i} className="px-2 py-0.5 bg-white rounded border border-gray-100 text-xs">
                {v}
              </span>
            ))}
          </div>
        ) : (
          value
        )}
      </div>
    </div>
  )
}

const SectionTitle = ({ title }: { title: string }) => (
  <h3 className="text-md font-bold text-[var(--color-primary-1)] mb-4 border-b pb-2 flex items-center gap-2">
    <div className="w-1.5 h-6 bg-[var(--color-primary-1)] rounded-full" />
    {title}
  </h3>
)

export const CreateContractConfirmationDialog = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.confirmation : textsData.en.confirmation;
  const { state, property, tenant } = useCreateContractContext();

  const ownerData = property?.owner
  const tenantData = tenant
  const addressData = property?.address
  
  // Map unity IDs to names
  const selectedUnitiesNames = property?.unities
    .filter((unity) => state.unitiesIds.includes(unity.id))
    .map(u => u.name) ?? []

  const formatDate = (dateString?: string | null | Date) => {
    if (!dateString) return '-';
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    return date.toLocaleDateString(userLanguage, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const currency = 'USD' // This could be dynamic in the future

  return (
    <div className="flex justify-center w-full py-4">
      <div className="bg-white rounded-2xl p-8 shadow-[0_10px_30px_rgba(0,0,0,0.1)] w-full max-w-[850px] border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          
          {/* Participants Card */}
          <div className="flex flex-col">
            <SectionTitle title={userLanguage === 'es' ? 'Participantes' : 'Participants'} />
            <div className="grid grid-cols-1 gap-3">
              <SummaryItem 
                label={texts.tenant} 
                value={`${tenantData?.firstName} ${tenantData?.lastName}`} 
                icon={personIcon}
              />
              <SummaryItem 
                label={texts.owner} 
                value={`${ownerData?.firstName} ${ownerData?.lastName}`} 
                icon={personIcon}
              />
            </div>
          </div>

          {/* Property Info */}
          <div className="flex flex-col">
            <SectionTitle title={userLanguage === 'es' ? 'Propiedad' : 'Property'} />
            <div className="grid grid-cols-1 gap-3">
              <SummaryItem label={texts.estate} value={property?.name || '-'} />
              <SummaryItem 
                label={texts.address} 
                value={addressData ? [addressData.street, addressData.city?.name, addressData.state?.name].filter(Boolean).join(', ') : '-'} 
                icon={addressIcon}
              />
              <SummaryItem 
                label={texts.unities} 
                value={selectedUnitiesNames.length > 0 ? selectedUnitiesNames : '-'} 
                icon={unitsIcon}
              />
            </div>
          </div>

          {/* Contract Terms */}
          <div className="flex flex-col">
            <SectionTitle title={userLanguage === 'es' ? 'Términos' : 'Terms'} />
            <div className="grid grid-cols-2 gap-3">
              <SummaryItem 
                label={texts.leaseTerm} 
                value={contractLeaseTermMapper(state.leaseTermOption, userLanguage)} 
                icon={termIcon}
                isFullWidth
              />
              <SummaryItem 
                label={texts.chargeStartDate} 
                value={formatDate(state.startDate)} 
                icon={calendarIcon}
              />
              <SummaryItem 
                label={texts.finishDate} 
                value={formatDate(state.finishDate)} 
                icon={calendarIcon}
              />
            </div>
          </div>

          {/* Financial Summary */}
          <div className="flex flex-col">
            <SectionTitle title={userLanguage === 'es' ? 'Finanzas' : 'Financials'} />
            <div className="grid grid-cols-2 gap-3">
              <SummaryItem 
                label={texts.monthlyAmount} 
                value={`${state.monthlyChargeAmount} ${currency}`} 
                icon={rentIcon}
                isFullWidth
              />
              <SummaryItem 
                label={texts.depositAmount} 
                value={`${state.depositAmount || 0} ${currency}`} 
                icon={depositIcon}
              />
              <SummaryItem 
                label={texts.depositReceivedDate} 
                value={formatDate(state.depositReceivedDate)} 
                icon={calendarIcon}
              />
            </div>
          </div>

          {/* Additional Charges */}
          {state.rentCharges && state.rentCharges.length > 0 && (
            <div className="md:col-span-2 mt-4">
              <SectionTitle title={texts.aditionalCharges} />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {state.rentCharges.map((charge, index) => (
                  <SummaryItem 
                    key={index}
                    label={charge.description} 
                    value={`${charge.amountValue} ${currency}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
